import { z } from 'zod'
import { religionSchema } from './employee.js'
import { roleSchema } from './role.js'

export interface ApiOk<T> {
  success: true
  data: T
  meta?: {
    total: number
    page: number
    limit: number
  }
}

export interface ApiErr {
  success: false
  error: string
  details?: unknown
}

export type ApiResponse<T> = ApiOk<T> | ApiErr

export const paginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(200).default(50),
})

export type PaginationQuery = z.infer<typeof paginationQuerySchema>

export const sessionUserSchema = z.object({
  id: z.guid(),
  email: z.string().email(),
  name: z.string(),
  role: z.string(),
  teamId: z.guid(),
  isManager: z.boolean(),
  isHod: z.boolean(),
  /** "M" | "F" | "X" — used by the frontend type picker to filter gender-gated leave types. */
  gender: z.enum(['M', 'F', 'X']).optional().catch(undefined),
  /** True when the credential account is flagged for rotation at next login.
   *  Set by either /api/v1/auth/password/reset or /force-change. The web routes
   *  the user to ChangePasswordRequiredPage and blocks other surfaces until
   *  /api/v1/auth/password/change clears it. SSO-only users — and older BE
   *  versions — omit the field entirely; consumers should treat missing as
   *  false (handled by `user?.mustChangePassword === true` at the call site). */
  mustChangePassword: z.boolean().optional(),
  /** True when the user has a local ("credential") account and can change a
   *  password in-app. SSO (Microsoft Entra) users have none — the web hides the
   *  Account / change-password surface for them. Optional: older BE versions and
   *  test fixtures omit it, so consumers treat only an explicit `false` as
   *  "SSO, hide it" (missing/undefined keeps the surface visible). */
  hasLocalPassword: z.boolean().optional(),
  /**
   * Multi-role assignment — the full set of roles this user holds. The API
   * always sends this from Slice 11 onward. Optional so existing sessions
   * (and the many test fixtures) that omit it still parse without error;
   * consumers should treat undefined as [].
   */
  roles: z.array(roleSchema).optional(),
})

export type SessionUser = z.infer<typeof sessionUserSchema>

/** GET /api/v1/auth/methods — the sign-in methods this environment accepts.
 *  Public (read before a session exists); the login page hides the
 *  email/password form when `password` is false. */
export const authMethodsSchema = z.object({
  password: z.boolean(),
  microsoft: z.boolean(),
})

export type AuthMethods = z.infer<typeof authMethodsSchema>

/** Minimal team reference embedded in Me. */
export const teamRefSchema = z.object({
  id: z.guid(),
  name: z.string(),
})

export type TeamRef = z.infer<typeof teamRefSchema>

/** Minimal approver reference embedded in Me. */
export const approverRefSchema = z.object({
  id: z.guid(),
  name: z.string(),
})

export type ApproverRef = z.infer<typeof approverRefSchema>

/** Full employee profile returned by GET /api/v1/me.
 *  Superset of SessionUser — includes fields not present in the auth cookie
 *  such as preferredName, jobTitle, and employmentType. */
export const meSchema = z.object({
  id: z.guid(),
  email: z.string().email(),
  name: z.string(),
  role: z.string(),
  /**
   * Multi-role assignment — the full set of roles this employee holds.
   * `role` (primary) is derived as the highest-ranked entry. Optional so
   * pre-multi-role API responses still parse; consumers treat undefined as [].
   */
  roles: z.array(roleSchema).optional(),
  teamId: z.guid(),
  /** Resolved team {id, name} — null only if the team row is missing. */
  team: teamRefSchema.nullable().optional(),
  /** Resolved line manager — null if no PRIMARY approver is assigned. */
  manager: approverRefSchema.nullable().optional(),
  jobTitle: z.string(),
  gender: z.enum(['M', 'F', 'X']),
  employmentType: z.string(),
  preferredName: z.string().nullable(),
  isManager: z.boolean(),
  isHod: z.boolean(),
  onProbation: z.boolean(),
  joinDate: z.string(),
  birthDate: z.string(),
  religion: religionSchema.nullable(),
  /** Fixed-term contract end date (ISO date string). Null for permanent employees. */
  contractEndDate: z.string().date().nullable().optional(),
  /** Resignation last-working-day (migration 0116). Distinct from
   *  contractEndDate (fixed-term contracts only) — applies to any
   *  employee type. Null when no resignation is on file. */
  lastWorkingDay: z.string().date().nullable().optional(),
  /** HR-set per-employee annual leave entitlement (policy §1.1, full
   *  grant). The AccrualBar drives its join/leaver proration off this
   *  full grant — the /me/balances row only carries the post-proration
   *  yearly slice. Optional for forward-compat with pre-migration
   *  servers. */
  annualEntitlement: z.number().int().nonnegative().optional(),
  /** True when the employee has an annual LeaveCreditOverride — a flat grant
   *  that bypasses accrual, so the AccrualBar hides. Optional for forward-compat
   *  with pre-migration servers. */
  hasAnnualOverride: z.boolean().optional(),
  /** Probation end date. Null when not on probation or not explicitly set. */
  probationEndDate: z.string().date().nullable().optional(),
  /** IANA timezone string. Defaults to 'Asia/Kuala_Lumpur' server-side. */
  timezone: z.string().nullable().optional(),
  /** Employee's declared hometown — used for distance-based scoring. */
  hometown: z.string().nullable().optional(),
  /** Distance in km from hometown to office — auto-computed by HR. */
  distanceKm: z.number().nullable().optional(),
  /** Whether the employee is married. */
  married: z.boolean().optional(),
  /** Number of dependent children. */
  children: z.number().int().nonnegative().optional(),
})

export type Me = z.infer<typeof meSchema>

/** Observed holiday entry returned by GET /api/v1/me/holiday-observance. */
export const observedHolidaySchema = z.object({
  date: z.string().date(),
  label: z.string(),
  observance: z.enum(['cultural', 'religious', 'national']),
})

export type ObservedHoliday = z.infer<typeof observedHolidaySchema>

export const myHolidayObservanceSchema = z.object({
  holidays: z.array(observedHolidaySchema),
})

export type MyHolidayObservance = z.infer<typeof myHolidayObservanceSchema>

/** Emergency contact returned by GET /api/v1/me/emergency-contact. */
export const emergencyContactSchema = z.object({
  name: z.string().nullable().optional(),
  phone: z.string().nullable().optional(),
  relationship: z.string().nullable().optional(),
  updatedAt: z.string().datetime().nullable().optional(),
})

export type EmergencyContact = z.infer<typeof emergencyContactSchema>

export const emergencyContactUpdateSchema = z.object({
  name: z.string().min(1),
  phone: z.string().min(1),
  relationship: z.string().nullable().optional(),
})

export type EmergencyContactUpdate = z.infer<typeof emergencyContactUpdateSchema>

/** Notification preferences returned by GET /api/v1/me/notification-preferences. */
export const notificationPreferencesSchema = z.object({
  leaveApprovedSlack: z.boolean(),
  leaveRejectedSlack: z.boolean(),
  leaveSubmittedSlack: z.boolean(),
  digestEmail: z.boolean(),
})

export type NotificationPreferences = z.infer<typeof notificationPreferencesSchema>

export const notificationPreferencesUpdateSchema = notificationPreferencesSchema.partial()

export type NotificationPreferencesUpdate = z.infer<typeof notificationPreferencesUpdateSchema>
